/** The builder's right-hand panels: widget picker, widget settings, history, page setup and theme. */

import type { ReactNode } from 'react';

import { KpiInserter } from '../kpis/KpiInserter.tsx';
import { AiSummarySettings } from './AiSummarySettings.tsx';
import type { Panel } from './BuilderBar.tsx';
import { CommentsPanel } from './CommentsPanel.tsx';
import { EmailPanel } from './email/EmailPanel.tsx';
import { ExportsPanel } from './ExportsPanel.tsx';
import { SetupPanel } from './SetupPanel.tsx';
import { SharePanel } from './SharePanel.tsx';
import type { ReportTheme } from './report-theme.ts';
import {
  reportsApi,
  type Catalogue,
  type DocumentSettings,
  type Page,
  type ReportDocument,
  type Widget,
  type WidgetResult,
} from './reports-api.ts';
import { ThemePanel } from './ThemePanel.tsx';
import type { useWidgetActions } from './use-widget-actions.ts';
import { VersionsPanel } from './VersionsPanel.tsx';
import { WidgetSettings } from './WidgetSettings.tsx';

export interface BuilderPanelsProps {
  panel: Panel;
  setPanel: (panel: Panel) => void;
  editable: boolean;
  organisationId: string;
  catalogue: Catalogue;
  document: ReportDocument;
  widgetActions: ReturnType<typeof useWidgetActions>;
  selected: number | null;
  page: Page;
  results: Record<string, WidgetResult>;
  keyOf: (index: number) => string;
  setWidgets: (widgets: Widget[]) => void;
  settings: DocumentSettings;
  setSettings: (settings: DocumentSettings) => void;
  theme: ReportTheme;
  setTheme: (theme: ReportTheme) => void;
  pages: Page[];
  pageIndex: number;
  onGoToPage: (index: number) => void;
  /** Opens another document (a share link's copy) in the builder. */
  onOpenDocument: (documentId: string) => void;
  /** After a version restore: the restored document and its pages. */
  onRestored: (document: ReportDocument, pages: Page[]) => void;
}

export function BuilderPanels(props: BuilderPanelsProps): ReactNode {
  const { panel, editable, organisationId, document, selected, page } = props;
  const close = () => props.setPanel(null);
  const selectedWidget = selected !== null ? page.widgets[selected] : undefined;
  if (panel === 'library' && editable)
    return (
      <KpiInserter
        organisationId={organisationId}
        clientId={document.business_unit_id}
        catalogue={props.catalogue}
        onPick={props.widgetActions.pick}
        onClose={close}
      />
    );
  if (panel === 'settings' && selectedWidget?.type === 'AI_SUMMARY' && editable)
    return (
      <AiSummarySettings
        key={selectedWidget.id ?? selected}
        organisationId={organisationId}
        document={document}
        settings={props.settings}
        page={page}
        widget={selectedWidget}
        onChange={(widget) =>
          props.setWidgets(page.widgets.map((item, index) => (index === selected ? widget : item)))
        }
        onClose={close}
      />
    );
  if (panel === 'settings' && selectedWidget && editable)
    return (
      <WidgetSettings
        key={selectedWidget.id ?? selected}
        widget={selectedWidget}
        catalogue={props.catalogue}
        result={props.results[props.keyOf(selected as number)]}
        onChange={(widget) =>
          props.setWidgets(page.widgets.map((item, index) => (index === selected ? widget : item)))
        }
        onClose={close}
      />
    );
  if (panel === 'versions')
    return (
      <VersionsPanel
        organisationId={organisationId}
        documentId={document.id}
        current={document.revision}
        onRestore={async (revision) => {
          const restored = await reportsApi.restore(
            organisationId,
            document.id,
            revision,
            document.revision,
          );
          props.onRestored(restored.document, restored.pages);
        }}
        onClose={close}
      />
    );
  if (panel === 'setup')
    return <SetupPanel settings={props.settings} onSettings={props.setSettings} onClose={close} />;
  if (panel === 'comments')
    return (
      <CommentsPanel
        organisationId={organisationId}
        documentId={document.id}
        pages={props.pages}
        pageIndex={props.pageIndex}
        selectedWidget={selectedWidget}
        onGoToPage={props.onGoToPage}
        onClose={close}
      />
    );
  if (panel === 'email')
    return (
      <EmailPanel
        organisationId={organisationId}
        documentId={document.id}
        documentName={document.name}
        range={props.settings.range ?? { preset: 'LAST_MONTH' }}
        onClose={close}
      />
    );
  if (panel === 'exports')
    return (
      <ExportsPanel
        organisationId={organisationId}
        documentId={document.id}
        documentName={document.name}
        range={props.settings.range ?? { preset: 'LAST_MONTH' }}
        onClose={close}
      />
    );
  if (panel === 'share')
    return (
      <SharePanel
        organisationId={organisationId}
        documentId={document.id}
        documentName={document.name}
        range={props.settings.range ?? { preset: 'LAST_MONTH' }}
        onEditCopy={props.onOpenDocument}
        onClose={close}
      />
    );
  if (panel === 'theme')
    return (
      <ThemePanel
        organisationId={organisationId}
        theme={props.theme}
        onTheme={props.setTheme}
        onClose={close}
      />
    );
  return null;
}
